import * as React from 'react';
import { Filter, X } from 'lucide-react';
import {
  DashboardDataTableColumnKind,
  buildDashboardDataTableColumns,
} from '@shigo-ui/dashboard-ui';
import { Button } from '@shigo-ui/ui';

import { dashboardT } from '../../../../lib/messages';
import { manageDialogFetchSize } from '../../../../lib/clientSelectionList';
import type { SupplyAppSelection, SupplyDomainSelection } from '../../../campaigns/supply/model';
import {
  SelectionDialog,
  type SelectionColumnOptions,
} from '../../../campaigns/supply/components/TailoredSupplyEditorPage/ManageSupplySelectionDialog';
import { truncatedSelectionValue } from '../../../campaigns/supply/components/TailoredSupplyEditorPage/SelectionDialog.helpers';
import { AppsFilters } from '../../../supply/apps/AppsFilters';
import { countActiveSupplyAppsSecondaryFilters } from '../../../supply/apps/appsRouteSearch';
import { useSupplyAppsQuery } from '../../../supply/apps/hooks';
import {
  defaultSupplyAppsListFilter,
  supplyAppsPageSizes,
  type SupplyInventoryApp,
} from '../../../supply/apps/model';
import { useTailoredSupplyDomainsQuery } from '../../../supply/tailored-supply/hooks';
import {
  defaultTailoredSupplyDomainsFilter,
  tailoredSupplyPageSizes,
  type TailoredSupplyDomain,
} from '../../../supply/tailored-supply/model';

const appsLookupFilter = {
  ...defaultSupplyAppsListFilter,
  searchTerm: '',
  publisherIds: [] as string[],
  platformIds: [] as string[],
  iabCategoryIds: [] as string[],
  page: 0,
  size: manageDialogFetchSize,
};

const domainsLookupFilter = {
  ...defaultTailoredSupplyDomainsFilter,
  searchTerm: '',
  publisherIds: [] as string[],
  iabCategoryIds: [] as string[],
  page: 0,
  size: manageDialogFetchSize,
};

export function ShigoAppsSelectionDialog({
  selectedItems,
  onConfirm,
  onOpenChange,
}: {
  selectedItems: SupplyAppSelection[];
  onConfirm: (items: SupplyAppSelection[]) => void;
  onOpenChange: (open: boolean) => void;
}) {
  const [publisherIds, setPublisherIds] = React.useState<string[]>([]);
  const [platformIds, setPlatformIds] = React.useState<string[]>([]);
  const [iabCategoryIds, setIabCategoryIds] = React.useState<string[]>([]);
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const clientFilter = {
    ...defaultSupplyAppsListFilter,
    publisherIds,
    platformIds,
    iabCategoryIds,
  };
  const activeFilterCount = countActiveSupplyAppsSecondaryFilters(clientFilter);
  const query = useSupplyAppsQuery(appsLookupFilter);
  return (
    <SelectionDialog
      open
      title={dashboardT('supply.apps')}
      description={dashboardT('supply.apps')}
      columns={buildShigoAppsColumns}
      errorMessage={query.isError ? dashboardT('supply.loadError') : undefined}
      getId={(app) => app.id}
      searchText={(app) => app.name}
      items={query.data?.content ?? []}
      loading={query.isLoading}
      minWidthClassName="min-w-[920px]"
      pageSizes={supplyAppsPageSizes}
      initialPageSize={25}
      clientFilterKey={[
        publisherIds.join(','),
        platformIds.join(','),
        iabCategoryIds.join(','),
      ].join('|')}
      matches={(app) => matchesShigoAppFilters(app, publisherIds, platformIds, iabCategoryIds)}
      selectedItems={selectedItems}
      toolbarActions={
        <div className="flex items-center">
          <Button
            type="button"
            variant="outline"
            className={activeFilterCount ? 'rounded-r-none' : undefined}
            aria-label={dashboardT('supplyApps.filters')}
            aria-expanded={filtersOpen}
            aria-controls="targeting-supply-apps-filter-toolbar"
            onClick={() => {
              setFiltersOpen((open) => !open);
            }}
          >
            <Filter className="h-4 w-4" aria-hidden="true" />
            {dashboardT('supplyApps.filters')}
            {activeFilterCount ? (
              <span className="bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 text-[10px] font-bold">
                {activeFilterCount}
              </span>
            ) : null}
          </Button>
          {activeFilterCount ? (
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive rounded-l-none border-l-0"
              aria-label={dashboardT('supplyApps.clearFilters')}
              onClick={() => {
                setPublisherIds([]);
                setPlatformIds([]);
                setIabCategoryIds([]);
              }}
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Button>
          ) : null}
        </div>
      }
      filterToolbar={
        filtersOpen ? (
          <div id="targeting-supply-apps-filter-toolbar">
            <AppsFilters
              filter={clientFilter}
              onPublishersChange={setPublisherIds}
              onPlatformsChange={setPlatformIds}
              onIabCategoriesChange={setIabCategoryIds}
            />
          </div>
        ) : undefined
      }
      toSelection={(app) => ({
        id: app.id,
        name: app.name,
        status: app.status,
        appStoreId: app.appStoreId,
        appStoreUrl: app.appStoreUrl,
        bundleId: app.bundleId,
        domain: app.domain,
        platform: app.platform,
        iabCategories: app.iabCategories.map((category) => category.id),
        iabCategoryNames: app.iabCategories.map((category) => category.name),
        createdOn: app.createdOn,
      })}
      onConfirm={onConfirm}
      onOpenChange={onOpenChange}
    />
  );
}

export function ShigoDomainsSelectionDialog({
  selectedItems,
  onConfirm,
  onOpenChange,
}: {
  selectedItems: SupplyDomainSelection[];
  onConfirm: (items: SupplyDomainSelection[]) => void;
  onOpenChange: (open: boolean) => void;
}) {
  const query = useTailoredSupplyDomainsQuery(domainsLookupFilter);
  return (
    <SelectionDialog
      open
      title={dashboardT('supply.domains')}
      description={dashboardT('supply.domains')}
      columns={buildShigoDomainsColumns}
      errorMessage={query.isError ? dashboardT('supply.loadError') : undefined}
      getId={(domain) => domain.id}
      searchText={(domain) => domain.url}
      items={query.data?.content ?? []}
      loading={query.isLoading}
      minWidthClassName="min-w-[640px]"
      pageSizes={tailoredSupplyPageSizes}
      selectedItems={selectedItems}
      toSelection={(domain) => ({ id: domain.id, url: domain.url, iabCategories: [] })}
      onConfirm={onConfirm}
      onOpenChange={onOpenChange}
    />
  );
}

function matchesShigoAppFilters(
  app: SupplyInventoryApp,
  publisherIds: string[],
  platformIds: string[],
  iabCategoryIds: string[],
) {
  const appPublisherIds = new Set(app.publisherIds);
  if (publisherIds.length && !publisherIds.some((id) => appPublisherIds.has(id))) {
    return false;
  }
  if (platformIds.length && (!app.platform || !platformIds.includes(app.platform.id))) {
    return false;
  }
  const selectedIabCategoryIds = new Set(iabCategoryIds);
  if (
    iabCategoryIds.length &&
    !app.iabCategories.some((category) => selectedIabCategoryIds.has(category.id))
  ) {
    return false;
  }
  return true;
}

function buildShigoAppsColumns({
  selectedIds,
  onToggleSelection,
  headerChecked,
  headerDisabled,
  headerLabel,
  onHeaderChange,
}: SelectionColumnOptions<SupplyInventoryApp>) {
  return buildDashboardDataTableColumns<SupplyInventoryApp>([
    {
      id: 'select',
      kind: DashboardDataTableColumnKind.Selection,
      checked: (app) => selectedIds.has(app.id),
      label: (app) => dashboardT('supply.selectNamed', { name: app.name }),
      onChange: onToggleSelection,
      headerChecked,
      headerDisabled,
      headerLabel,
      onHeaderChange,
    },
    {
      id: 'name',
      header: dashboardT('supply.name'),
      kind: DashboardDataTableColumnKind.Custom,
      headerClassName: 'w-56',
      className: 'w-56 max-w-56',
      cell: (app) => truncatedSelectionValue(app.name),
    },
    {
      id: 'status',
      header: dashboardT('supply.status'),
      kind: DashboardDataTableColumnKind.Text,
      value: (app) => app.status,
    },
    {
      id: 'appStoreId',
      header: dashboardT('supply.appStoreId'),
      kind: DashboardDataTableColumnKind.Text,
      value: (app) => app.appStoreId,
    },
    {
      id: 'platform',
      header: dashboardT('supply.platform'),
      kind: DashboardDataTableColumnKind.Text,
      value: (app) => app.platform?.name,
    },
    {
      id: 'bundleId',
      header: dashboardT('supply.bundleId'),
      kind: DashboardDataTableColumnKind.Custom,
      headerClassName: 'w-56',
      className: 'w-56 max-w-56',
      cell: (app) => truncatedSelectionValue(app.bundleId),
    },
  ]);
}

function buildShigoDomainsColumns({
  selectedIds,
  onToggleSelection,
  headerChecked,
  headerDisabled,
  headerLabel,
  onHeaderChange,
}: SelectionColumnOptions<TailoredSupplyDomain>) {
  return buildDashboardDataTableColumns<TailoredSupplyDomain>([
    {
      id: 'select',
      kind: DashboardDataTableColumnKind.Selection,
      checked: (domain) => selectedIds.has(domain.id),
      label: (domain) => dashboardT('supply.selectNamed', { name: domain.url }),
      onChange: onToggleSelection,
      headerChecked,
      headerDisabled,
      headerLabel,
      onHeaderChange,
    },
    {
      id: 'name',
      header: dashboardT('supply.url'),
      kind: DashboardDataTableColumnKind.Text,
      className: 'font-medium',
      value: (domain) => domain.url,
    },
    {
      id: 'publisher',
      header: dashboardT('tailoredSupply.publisher'),
      kind: DashboardDataTableColumnKind.Text,
      value: (domain) => domain.publisher?.name,
    },
  ]);
}
